import {
  Button,
  SplitButton,
  TextBox,
  useValidation,
} from "@etframework/ui.components";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { FieldTypeSortableList } from "./FieldTypeSortableList";
import { useTranslation } from "react-i18next";
import { useCreateOrUpdateCustomFieldGroup } from "../../data/mutations/customFieldGroupMutation";
import { toast } from "react-toastify";
import useModalEvent from "../../hooks/useModalEvent";
import { isGuid } from "../../utils/helpers";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

export const FieldGroupCreateContainer = forwardRef(
  ({ modalRef, fieldGroup = {}, setCustomFieldGroupId, setIsUpdate }, ref) => {
    const { t } = useTranslation(["CustomFieldGroup"], {
      useSuspense: false,
    });

    const { taskTypeId } = useParams();

    const { setValidationErrors, clearValidationErrors } = useValidation();
    const { ModalEvents } = useModalEvent();
    const queryClient = useQueryClient();

    const id = ModalEvents.CustomFieldGroupModal.Id;
    const ids = {
      customFieldGroup: id + "_custom-field-group",
      saveButton: id + "_save-button",
    };

    //Refs
    const fieldGroupRef = useRef(null);
    const fieldListRef = useRef(null);

    //States
    const [persistentData, setPersistentData] = useState(fieldGroup);

    //Mutations
    const { mutate: createOrUpdateCustomFieldGroup, isPending } =
      useCreateOrUpdateCustomFieldGroup();

    const saveAndContinueBtnItems = [
      {
        text: t("SaveAndClose"),
        iconCss: "",
        onClick: async (e) => {
          onCustomFieldGroupModified(e, true);
        },
      },
    ];
    const onSelectDropDownTaskMenu = (args) => {
      args?.item?.onClick?.();
    };

    const onCloseModal = () => {
      clearValidationErrors();
      modalRef.current?.close();
    };

    const onCustomFieldGroupModified = (args, saveAndClose = false) => {
      var postData = {
        Name: fieldGroupRef.current?.getValue(),
        TaskTypeId: taskTypeId,
        CustomFields: fieldListRef.current
          ?.getInputValues()
          .map((customField) => {
            return {
              ...customField,
              Id: isGuid(customField?.Id) ? customField?.Id : null,
            };
          }),
        Visible: persistentData?.Visible ?? true,
        Id: persistentData?.Id ?? null,
      };

      setPersistentData({
        ...postData,
      });

      createOrUpdateCustomFieldGroup(
        { customFieldGroup: postData },
        {
          onSuccess: (response) => {
            setPersistentData(response.UpdatedEntity);
            clearValidationErrors();

            queryClient.invalidateQueries([
              "get-custom-field-group",
              persistentData?.Id,
            ]);

            fieldListRef.current?.setInputValues(
              response.UpdatedEntity?.CustomFields
            );

            toast.success(
              t(`${persistentData?.Id ? "SuccessUpdate" : "SuccessCreation"}`),
              {
                position: "bottom-left",
              }
            );

            if (saveAndClose) {
              onCloseModal();
            }
          },
          onError: (errors) => {
            setValidationErrors(errors?.data);
            toast.error(t("ErrorMsg"), { position: "bottom-left" });
          },
        }
      );
    };

    useImperativeHandle(
      ref,
      () => ({
        clearValidationErrors,
      }),
      [clearValidationErrors]
    );

    return (
      <div className="create-custom-fields row px-3">
        <div className="d-flex justify-content-between align-items-center px-4 pt-3">
          <div className="col-6">
            <TextBox
              autoFocus
              name="Name"
              ref={fieldGroupRef}
              labelText={t("FieldGroupName")}
              id={ids.customFieldGroup}
              value={persistentData?.Name}
            />
          </div>

          <div className="d-flex justify-content-end align-items-center gap-3">
            <Button
              text={t("Cancel")}
              className="btn-tertiary"
              onClick={() => {
                if (persistentData?.Id) {
                  setIsUpdate(true);
                  setCustomFieldGroupId(persistentData?.Id);
                } else {
                  onCloseModal();
                }
              }}
            />
            <SplitButton
              id={ids.saveButton}
              items={saveAndContinueBtnItems}
              content={t("SaveAndContinue")}
              className="btn btn-primary"
              onClick={(e) => {
                onCustomFieldGroupModified(e, false);
              }}
              select={onSelectDropDownTaskMenu}
              loading={isPending}
            />
          </div>
        </div>

        <div className="px-4 mt-1">
          <FieldTypeSortableList
            ref={fieldListRef}
            customFields={persistentData?.CustomFields ?? []}
          />
        </div>
      </div>
    );
  }
);
